import { freshState, type GameState, type GameType } from "./game-core";
import { newGlassState, parseGlassSave, type GlassState } from "./stained-glass-core";
import { newReef, parseReefSave, type ReefState } from "./coral-chorus-core";
import { createInitialGlassRoomState } from "./stained-glass-room";
import { createInitialCoralRoomState } from "./coral-chorus-room";

export const ASYNC_GAMES: readonly GameType[] = ["connect_four", "ticket_to_ride", "axis_allies", "stratego", "modern_art", "catan"];
export const LIBRARY_LIMIT = 100;
export interface SavedRoom { id: string; title: string; asyncTurns: boolean }
export interface LibraryMetadata {
  id: string; title: string; kind: "campaign" | "art"; gameType: GameType; savedAt: number;
  roomCode: string; campaignId: string; contributors: string[]; pieceCount: number; asyncTurns: boolean;
}
export interface LibraryEntry { metadata: LibraryMetadata; snapshot: GameState }
export interface LibraryStorage {
  get<T>(key: string): Promise<T | undefined>;
  put(key: string, value: unknown): Promise<void>;
  delete(key: string): Promise<unknown>;
}
const libraryWrites = new WeakMap<LibraryStorage, Promise<void>>();
export function validLibraryKey(key: unknown): key is string {
  return typeof key === "string" && /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(key);
}
export function isCreativeGame(type: GameType) { return type === "stained_glass" || type === "coral_chorus"; }

export function createLibraryEntry(game: GameState, playerId: string, isAdmin: boolean, title: unknown, now = Date.now()): LibraryEntry | string {
  if (!game.players[playerId]?.connected) return "Join the room before saving.";
  if (game.players[playerId].sittingOut && !isAdmin) return "Join the game before saving.";
  const art = isCreativeGame(game.gameType);
  if (!art && !isAdmin) return "Only the host can save a campaign.";
  if (!art && !ASYNC_GAMES.includes(game.gameType) && !["market_wars", "king_of_tokyo", "king_of_the_court"].includes(game.gameType)) return "Named campaigns are available for the board games in the local launcher.";
  const scene = game.gameType === "stained_glass" ? game.stainedGlass.scene : game.coralChorus.scene;
  const name = typeof title === "string" ? title.trim().slice(0, 60) : "";
  if (!name) return "Give this save a name.";
  const savedRoom = game.savedRoom || { id: crypto.randomUUID(), title: name, asyncTurns: false };
  const snapshot = art ? freshState(game.roomCode, game.gameType, game.adminId, "") : structuredClone(game);
  if (art) {
    snapshot.exists = true; snapshot.phase = game.phase;
    snapshot.players = structuredClone(game.players);
    if (game.gameType === "stained_glass") snapshot.stainedGlass = structuredClone(game.stainedGlass);
    else snapshot.coralChorus = structuredClone(game.coralChorus);
  }
  snapshot.savedRoom = { ...savedRoom, title: name };
  // Transport identity and chat do not belong in a portable checkpoint or artwork link.
  snapshot.adminToken = ""; snapshot.chat = freshState().chat;
  for (const player of Object.values(snapshot.players)) player.connected = false;
  return { metadata: { id: crypto.randomUUID(), title: name, kind: art ? "art" : "campaign", gameType: game.gameType,
    savedAt: now, roomCode: game.roomCode, campaignId: savedRoom.id, contributors: art ? [...scene.players] : Object.values(game.players).map(p => p.name),
    pieceCount: art ? scene.pieces.length : 0, asyncTurns: savedRoom.asyncTurns }, snapshot };
}

/** Chunk snapshots below the Durable Object value limit, including large glass histories. */
export class SavedLibrary {
  constructor(private storage: LibraryStorage) {}
  private async mutate<T>(operation: () => Promise<T>): Promise<T> {
    const previous = libraryWrites.get(this.storage) || Promise.resolve();
    let release!: () => void;
    const gate = new Promise<void>(resolve => { release = resolve; });
    libraryWrites.set(this.storage, previous.then(() => gate));
    await previous;
    try { return await operation(); } finally { release(); }
  }
  async list(): Promise<LibraryMetadata[]> { return await this.storage.get<LibraryMetadata[]>("library:index") || []; }
  async save(entry: LibraryEntry) {
    return this.mutate(async () => {
    const index = await this.list();
    if (index.length >= LIBRARY_LIMIT && !index.some(item => item.id === entry.metadata.id)) throw new Error("Your library has 100 entries. Remove an old save before adding another.");
    const content = JSON.stringify(entry.snapshot), size = 16_000, chunks = Math.ceil(content.length / size);
    if (content.length > 2_000_000) throw new Error("This save exceeds the 2 MB library limit.");
    for (let i = 0; i < chunks; i++) await this.storage.put(`library:${entry.metadata.id}:${i}`, content.slice(i * size, (i + 1) * size));
    await this.storage.put(`library:${entry.metadata.id}:chunks`, chunks);
    await this.storage.put("library:index", [entry.metadata, ...index.filter(item => item.id !== entry.metadata.id)]);
    });
  }
  async read(id: string): Promise<LibraryEntry | undefined> {
    const metadata = (await this.list()).find(item => item.id === id);
    if (!metadata) return;
    const chunks = await this.storage.get<number>(`library:${id}:chunks`);
    if (!chunks) return;
    let content = "";
    for (let i = 0; i < chunks; i++) {
      const part = await this.storage.get<string>(`library:${id}:${i}`); if (part === undefined) throw new Error("The saved checkpoint is incomplete."); content += part;
    }
    return { metadata, snapshot: JSON.parse(content) as GameState };
  }
  async remove(id: string) {
    return this.mutate(async () => {
    const index = await this.list(), chunks = await this.storage.get<number>(`library:${id}:chunks`) || 0;
    await this.storage.put("library:index", index.filter(item => item.id !== id));
    for (let i = 0; i < chunks; i++) await this.storage.delete(`library:${id}:${i}`);
    await this.storage.delete(`library:${id}:chunks`);
    });
  }
}

export function artworkView(entry: LibraryEntry) {
  if (entry.metadata.kind !== "art") return null;
  return { ...entry.metadata, scene: structuredClone(entry.snapshot.gameType === "stained_glass" ? entry.snapshot.stainedGlass.scene : entry.snapshot.coralChorus.scene) };
}

export function copiedLibraryEntry(original: LibraryEntry, game: GameState): LibraryEntry {
  const snapshot = structuredClone(game); snapshot.adminToken = ""; snapshot.chat = freshState().chat;
  for (const player of Object.values(snapshot.players)) player.connected = false;
  const scene = game.gameType === "stained_glass" ? game.stainedGlass.scene : game.coralChorus.scene;
  return { snapshot, metadata: { ...original.metadata, id: crypto.randomUUID(), title: game.savedRoom!.title,
    campaignId: game.savedRoom!.id, roomCode: game.roomCode, savedAt: Date.now(), asyncTurns: game.savedRoom!.asyncTurns,
    contributors: original.metadata.kind === "art" ? [...scene.players] : Object.values(game.players).map(player => player.name) } };
}

export function restoreLibraryEntry(entry: LibraryEntry, code: string, creatorId: string, token: string, remix = false): GameState {
  if (entry.metadata.kind === "campaign" && creatorId !== entry.snapshot.adminId) throw new Error("Use the same browser identity that saved this campaign.");
  const game = entry.metadata.kind === "art" ? freshState(code, entry.metadata.gameType, creatorId, token) : structuredClone(entry.snapshot);
  game.exists = true; game.roomCode = code; game.adminId = creatorId; game.adminToken = token; game.lastActive = Date.now();
  if (game.gameType === "market_wars") game.deadline = game.marketWars.status === "playing" && !game.marketWars.paused ? Date.now() + game.marketWars.settings.secondsPerQuarter * 1000 / 12 : null;
  game.savedRoom = { id: crypto.randomUUID(), title: `${entry.metadata.title}${remix ? " (remix)" : ""}`.slice(0, 60), asyncTurns: entry.metadata.asyncTurns };
  if (entry.metadata.kind === "art") {
    const glass = game.gameType === "stained_glass";
    const saved = glass ? entry.snapshot.stainedGlass : entry.snapshot.coralChorus;
    // Credit the original creators without granting their old identities room access.
    const order = saved.scene.players.map((_, index) => `credit-${entry.metadata.id}-${index}`);
    if (glass) {
      game.stainedGlass = { ...createInitialGlassRoomState(), scene: structuredClone(saved.scene) as GlassState, playerOrder: order };
      game.stainedGlass.scene.pieces.forEach(piece => piece.playerId = order[piece.player]);
      game.stainedGlass.scene.finished = false; game.stainedGlass.scene.title = game.savedRoom.title;
    } else {
      game.coralChorus = { ...createInitialCoralRoomState(), scene: structuredClone(saved.scene) as ReefState, playerOrder: order };
      game.coralChorus.scene.admire = false; game.coralChorus.scene.title = game.savedRoom.title;
    }
    game.phase = glass ? "stained_glass_playing" : "coral_chorus_playing";
  }
  for (const player of Object.values(game.players)) player.connected = game.gameType === "market_wars" && game.marketWars.botIds.includes(player.id);
  game.players[creatorId] ||= { id: creatorId, name: "You", score: 0, connected: false, joinedAt: Date.now() };
  return game;
}

export function offlineArtEntry(type: unknown, scene: unknown): LibraryEntry | string {
  if (type !== "stained_glass" && type !== "coral_chorus") return "Choose a creative game.";
  if (!scene || typeof scene !== "object" || (scene as GlassState).version !== 1 || !Array.isArray((scene as GlassState).pieces)) return "Invalid artwork.";
  const raw = JSON.stringify(scene);
  const parsed = type === "stained_glass" ? parseGlassSave(raw) : parseReefSave(raw);
  if (parsed.pieces.length !== (scene as GlassState).pieces.length) return "Some placements in this artwork are invalid.";
  const game = freshState("", type, "offline", "");
  game.players.offline = { id: "offline", name: "Maker", connected: true, score: 0, joinedAt: Date.now() };
  if (type === "stained_glass") game.stainedGlass.scene = parsed as GlassState;
  else game.coralChorus.scene = parsed as ReefState;
  return createLibraryEntry(game, "offline", true, parsed.title);
}
